import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Input,
  Typography,
} from '@mui/material'
import { useRef, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DownloadIcon from '@mui/icons-material/Download'
import PauseIcon from '@mui/icons-material/Pause'
import DeleteIcon from '@mui/icons-material/Delete'
import AddVehicleDialogBody from './addVehicleDialogBody'
import { Tooltip } from 'components'
import { MappedHeartbeatVehicle } from 'interfaces/ICarrierHeartbeat'
import strings from 'strings'

const t = strings.TABLE_DEFAULTS

interface DialogConfig {
  type: 'success' | 'warning' | 'delete' | 'none'
  title: string
  body1: string
  body2?: string
  action: () => Promise<void>
  primaryBtnProps?: {
    label: string
    color?: 'primary' | 'error'
  }
}

interface ICarrierHeartbeatTableToolbarProps {
  handlers: {
    uploadCsv: (file: File, isActive: number) => void
    showDialog: (config: DialogConfig, contentNode?: React.ReactNode) => void
    downloadTemplate?: () => void
    addVehicle: (identificationNoList: string[], isActive: number) => void
    clearVehicle: (identificationNoList: string[], isActive: number) => void
    pauseVehicle: (
      identificationNoList: string[],
      assetEldIdList: (number | string)[],
      isActive: number
    ) => void
  }
  policyFilter: string
  setPolicyFilter: (value: string) => void
  telematicsFilter: string
  setTelematicsFilter: (value: string) => void
  stateFilter: string
  setStateFilter: (value: string) => void
  periodFilter: string
  setPeriodFilter: (value: string) => void
  isActive: number
  isBulkAction: boolean
  selectedRowsData: MappedHeartbeatVehicle[]
  isReadonlyDashboard: boolean
}

const CarrierHeartbeatTableToolbar: React.FC<ICarrierHeartbeatTableToolbarProps> =
  ({
    handlers,
    policyFilter,
    setPolicyFilter,
    telematicsFilter,
    setTelematicsFilter,
    stateFilter,
    setStateFilter,
    periodFilter,
    setPeriodFilter,
    isActive,
    isBulkAction,
    selectedRowsData,
    isReadonlyDashboard
  }) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [currentVin, setCurrentVin] = useState('')

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0]
      if (file) {
        handlers.uploadCsv(file, isActive)

        event.target.value = ''
      }
    }

    const handleClearPolicy = () => {
      handlers.showDialog({
        type: 'warning',
        title: 'Clear Policy List?',
        body1:
          'This will remove all vehicles from your policy. Vehicles without telematics data will be completely removed from the list. This action cannot be undone.',
        action: async () => {
          handlers.clearVehicle([], isActive)
        },
        primaryBtnProps: {
          label: 'Clear Policy',
          color: 'error',
        },
      })
    }

    const handleAddVehicle = () => {
      let vinToAdd = ''

      handlers.showDialog(
        {
          type: 'none',
          title: 'Add New Vehicle',
          body1: '',
          action: async () => {
            if (vinToAdd) {
              await handlers.addVehicle([vinToAdd], isActive)
            }
          },
          primaryBtnProps: {
            label: 'Add Vehicle',
            color: 'primary',
          },
        },
        <AddVehicleDialogBody
          onSubmit={(vin) => {
            vinToAdd = vin
          }}
        />
      )
    }

    const createBulkPauseDialogBody = (vehicles: MappedHeartbeatVehicle[]) => {
      const count = vehicles.length
      return (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to {isActive === 1 ? 'pause' : 'resume'}{' '}
            <strong>{count}</strong> vehicle
            {count > 1 ? 's' : ''}.
          </Typography>
          <Typography variant="body1">
            This will {isActive === 1 ? 'stop' : 'resume'} telematics data
            collection for the selected vehicles.
          </Typography>
        </>
      )
    }

    const createBulkRemoveDialogBody = (vehicles: MappedHeartbeatVehicle[]) => {
      const count = vehicles.length
      return (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to remove <strong>{count}</strong> vehicle
            {count > 1 ? 's' : ''} from your policy.
          </Typography>
          <Typography variant="body1">
            This will completely remove the selected vehicles from your policy
            list. This action cannot be undone without re-uploading the vehicles
            through CSV or manually adding them again.
          </Typography>
        </>
      )
    }

    const createBulkAddDialogBody = (vehicles: MappedHeartbeatVehicle[]) => {
      const count = vehicles.length
      return (
        <>
          <Typography variant="body1" sx={{ mb: 2 }}>
            You are about to add <strong>{count}</strong> vehicle
            {count > 1 ? 's' : ''} to your policy.
          </Typography>
        </>
      )
    }

    const handleBulkPause = () => {
      handlers.showDialog(
        {
          type: 'warning',
          title:
            isActive === 1
              ? 'Pause Selected Vehicles'
              : 'Resume Selected Vehicles',
          body1: '',
          action: async () => {
            const assetEldIdList = selectedRowsData.flatMap((vehicle) =>
              vehicle.assetEldId && vehicle.assetEldId !== t.DASH_VALUE
                ? [vehicle.assetEldId]
                : []
            )

            const identificationNoList = selectedRowsData.flatMap((vehicle) =>
              vehicle.identificationNo &&
              vehicle.identificationNo !== t.DASH_VALUE &&
              (!vehicle.assetEldId || vehicle.assetEldId === t.DASH_VALUE)
                ? [vehicle.identificationNo]
                : []
            )
            handlers.pauseVehicle(
              identificationNoList,
              assetEldIdList,
              isActive
            )
          },
          primaryBtnProps: {
            label: isActive === 1 ? 'Pause Vehicles' : 'Resume Vehicles',
            color: 'primary',
          },
        },
        createBulkPauseDialogBody(selectedRowsData)
      )
    }

    const handleBulkRemove = () => {
      handlers.showDialog(
        {
          type: 'delete',
          title: 'Remove Selected Vehicles',
          body1: '',
          action: async () => {
            const identificationNoList = selectedRowsData.flatMap((vehicle) =>
              vehicle.identificationNo &&
              vehicle.identificationNo !== t.DASH_VALUE
                ? [vehicle.identificationNo]
                : []
            )
            handlers.clearVehicle(identificationNoList, isActive)
          },
          primaryBtnProps: {
            label: 'Remove Vehicles',
            color: 'error',
          },
        },
        createBulkRemoveDialogBody(selectedRowsData)
      )
    }

    const handleBulkAdd = () => {
      handlers.showDialog(
        {
          type: 'warning',
          title: 'Add Selected Vehicles',
          body1: '',
          action: async () => {
            const identificationNoList = selectedRowsData.flatMap((vehicle) =>
              vehicle.identificationNo &&
              vehicle.identificationNo !== t.DASH_VALUE
                ? [vehicle.identificationNo]
                : []
            )
            handlers.addVehicle(identificationNoList, isActive)
          },
          primaryBtnProps: {
            label: 'Add Vehicles',
            color: 'primary',
          },
        },
        createBulkAddDialogBody(selectedRowsData)
      )
    }

    const isBulkRemoveDisabled = (
      vehicles: MappedHeartbeatVehicle[]
    ): boolean => {
      return vehicles.some((vehicle) => vehicle.policyList === false)
    }

    const isBulkAddDisabled = (vehicles: MappedHeartbeatVehicle[]): boolean => {
      return vehicles.some(
        (vehicle) =>
          vehicle.telematics === false ||
          vehicle.policyList === true ||
          vehicle.identificationNo === t.DASH_VALUE
      )
    }

    const isBulkPauseDisabled = (
      vehicles: MappedHeartbeatVehicle[]
    ): boolean => {
      return vehicles.some((vehicle) => vehicle.telematics === false)
    }

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          p: 2,
          pb: 0,
        }}
      >
        <Input
          type="file"
          inputRef={fileInputRef}
          sx={{ display: 'none' }}
          inputProps={{
            accept: '.csv',
          }}
          onChange={handleFileChange}
        />
        {/* Left: Filters */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            flexWrap: 'wrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="policy-select-label">Policy</InputLabel>
            <Select
              labelId="policy-select-label"
              id="policy-select"
              value={policyFilter}
              label="Policy"
              onChange={(e) => setPolicyFilter(e.target.value)}
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="true">On Policy</MenuItem>
              <MenuItem value="false">Out of Policy</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="telematics-select-label">Telematics</InputLabel>
            <Select
              labelId="telematics-select-label"
              id="telematics-select"
              value={telematicsFilter}
              label="Telematics"
              onChange={(e) => setTelematicsFilter(e.target.value)}
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="true">With Telematics</MenuItem>
              <MenuItem value="false">Without Telematics</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="state-select-label">Moving State</InputLabel>
            <Select
              labelId="state-select-label"
              id="state-select"
              value={stateFilter}
              label="Moving State"
              onChange={(e) => setStateFilter(e.target.value)}
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="true">Moving in</MenuItem>
              <MenuItem value="false">Not Moving in</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel id="period-select-label">Moving Period</InputLabel>
            <Select
              labelId="period-select-label"
              id="period-select"
              value={periodFilter}
              label="Moving Period"
              onChange={(e) => setPeriodFilter(e.target.value)}
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="30">Last 30 Days</MenuItem>
              <MenuItem value="60">Last 60 Days</MenuItem>
              <MenuItem value="90">Last 90 Days</MenuItem>
            </Select>
          </FormControl>
        </Box>
        {/* Right: Action Buttons */}
        {!isReadonlyDashboard && (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}
        >
          {!isBulkAction ? (
            <>
              <Button
                variant="outlined"
                color="error"
                sx={{ textTransform: 'none' }}
                onClick={handleClearPolicy}
              >
                Clear Policy
              </Button>
              <Button
                variant="contained"
                sx={{ textTransform: 'none' }}
                onClick={handleAddVehicle}
                startIcon={<AddIcon />}
              >
                Add Vehicle
              </Button>
            </>
          ) : (
            <>
              <Tooltip
                title={
                  isBulkPauseDisabled(selectedRowsData)
                    ? 'Only vehicles with telematics data can be ' +
                      (isActive === 1 ? 'paused' : 'resumed')
                    : ''
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="warning"
                    sx={{ textTransform: 'none' }}
                    onClick={handleBulkPause}
                    startIcon={<PauseIcon />}
                    disabled={isBulkPauseDisabled(selectedRowsData)}
                  >
                    Bulk {isActive === 1 ? 'Pause' : 'Resume'}
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  isBulkRemoveDisabled(selectedRowsData)
                    ? 'Only vehicles on policy can be removed'
                    : ''
                }
              >
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{ textTransform: 'none' }}
                    onClick={handleBulkRemove}
                    startIcon={<DeleteIcon />}
                    disabled={isBulkRemoveDisabled(selectedRowsData)}
                  >
                    Bulk Remove
                  </Button>
                </span>
              </Tooltip>
              <Tooltip
                title={
                  isBulkAddDisabled(selectedRowsData)
                    ? 'Only vehicles with telematics data, valid VIN, and not already on policy can be added'
                    : ''
                }
              >
                <span>
                  <Button
                    variant="contained"
                    sx={{ textTransform: 'none' }}
                    onClick={handleBulkAdd}
                    startIcon={<AddIcon />}
                    disabled={isBulkAddDisabled(selectedRowsData)}
                  >
                    Bulk Add
                  </Button>
                </span>
              </Tooltip>
            </>
          )}

          <Button
            variant="outlined"
            sx={{ textTransform: 'none' }}
            onClick={() => fileInputRef.current?.click()}
            startIcon={<UploadFileIcon />}
          >
            Upload CSV
          </Button>
          {!isBulkAction && (
            <Tooltip title="Download a CSV template with example VINs. Replace them with your own data before uploading.">
              <Button
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={handlers.downloadTemplate}
                startIcon={<DownloadIcon />}
              >
                Download Template
              </Button>
            </Tooltip>
          )}
        </Box>
        )}
      </Box>
    )
  }

export default CarrierHeartbeatTableToolbar
